import { action, observable } from "mobx";
import {
    TableViewStore,
    BaseListViewStore,
    BaasicDropdownStore,
    BasicQueryInputStore
} from 'core/stores';
import applicationContext from 'core/utils/applicationContext';
import {FacilityPumpService} from 'application/pumps/services';
import {FacilityService} from 'application/facility/services';
import {ProductService} from 'application/product/services';
import { toDate } from 'core/utils';
import {GridBooleanCell} from 'core/components';
import { InventoryFilter } from 'application/inventory/filters';
import {CorporateService} from 'application/corporate/services';
import _ from 'lodash';

const orderByMap = {
    corporate: 'facility.corporate_',
    masterPatientId: 'facility.masterPatientId'
};
const reverseOrderByMap = _.invert(orderByMap);

@applicationContext
class InventoryViewStore extends BaseListViewStore {
    @observable accessor downloadUrl = null;

    constructor(rootStore) {
        const service = new FacilityPumpService(rootStore.application.baasic.apiClient);
        const productService = new ProductService(rootStore.application.baasic.apiClient);
        const facilityService = new FacilityService(rootStore.application.baasic.apiClient);
        const corporateService = new CorporateService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            name: 'product',
            autoInit: true,
            authorization: {
                create: 'portalSection.create',
                delete: 'portalSection.delete',
            },
            queryConfig: {
                filter: new InventoryFilter(),
            },
            routes: {
            },
            actions: () => {
                return {
                    find: async (params) => {
                        const orderBy = orderByMap[params.orderBy] || params.orderBy;
                        const response = await service.find(_.extend(params, { orderBy, embed: 'Facility,PumpStatus,Product' }));
                        if (response.data && response.data.sort) {
                            const [respOrderBy, respDirection] = response.data.sort.split('|');
                            if (reverseOrderByMap[respOrderBy]) {
                                response.data.sort = `${reverseOrderByMap[respOrderBy]}|${respDirection}`;
                            }
                        }
                        const res = _.extend(response.data, {
                            item: _.map(response.data.item, (item => {
                                return _.extend(
                                    item, {
                                        pumpStatus: item.pumpStatus.name,
                                        product: item.product.itemId,
                                        facility: item.facility.name,
                                        masterPatientId: item.facility.masterPatientId,
                                        corporate: item.facility && item.facility.corporate_ && item.facility.corporate_.name,
                                        dateSent: toDate(item.dateSent),
                                        dateReturnRequested: toDate(item.dateReturnRequested),
                                        dateReturned: toDate(item.dateReturned)
                                    }
                                );
                            }))
                        });                       
                        this.onFindSuccess();

                        return res;
                    }
                }
            }
        });

        this.service = service;

        this.setTableStore(new TableViewStore(this.queryUtility, {
            columns: [
                {
                    key: 'facility',
                    title: 'INVENTORY.LIST.COLUMNS.FACILITY'
                },
                {
                    key: 'masterPatientId',
                    title: 'INVENTORY.LIST.COLUMNS.ACCOUNT_NUMBER',
                    width: 140
                },
                {
                    key: 'corporate',
                    title: 'INVENTORY.LIST.COLUMNS.CORPORATE'
                },
                {
                    key: 'product',
                    title: 'INVENTORY.LIST.COLUMNS.PRODUCT',
                    width: 160
                },
                {
                    key: 'serialNumber',
                    title: 'INVENTORY.LIST.COLUMNS.SERIAL_NUMBER',
                    width: 120
                },
                {
                    key: 'pumpStatus',
                    title: 'INVENTORY.LIST.COLUMNS.STATUS',
                    width: 140,
                    headerClassName: "center",
                    className: "type--center"
                },
                {
                    key: 'stickered',
                    title: 'INVENTORY.LIST.COLUMNS.STICKERED',
                    cell: GridBooleanCell,
                    width: 90,
                    headerClassName: "center"
                },
                {
                    key: 'branch',
                    title: 'INVENTORY.LIST.COLUMNS.BRANCH',
                    width: 100
                },
                {
                    key: 'dateSent',
                    title: 'INVENTORY.LIST.COLUMNS.DATE_SENT',
                    width: 100
                },
                {
                    key: 'dateReturnRequested',
                    title: 'INVENTORY.LIST.COLUMNS.DATE_RETURN_REQUESTED',
                    width: 180
                },
                {
                    key: 'dateReturned',
                    title: 'INVENTORY.LIST.COLUMNS.DATE_RETURNED',
                    width: 120
                }
            ],
            actions: {
            }
        }));

        this.searchStore = new BasicQueryInputStore();

        this.queryUtility.onResetFilter = ()=>{
            this.facilityStore.setValue(null);
            this.facilityStore.filterTerm = "";

            this.pumpStore.setValue(null);      

            this.corporateStore.setValue(null);    
            this.corporateStore.filterTerm = "";
            this.searchStore.reset();    
        }

        this.corporateStore = new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async(term) => {
                const response = await corporateService.find({
                    isActive: true,
                    searchQuery: term, 
                    rpp: 30
                });
                return response.data.item;
            }
        });

        this.facilityStore = new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await facilityService.find({ searchQuery: term, rpp: 500, inActive: false });
                return response.data.item;
            }
        });

        this.pumpStore = new BaasicDropdownStore({
            textField: 'itemId'
        }, {
            fetchFunc: async() => {
                const response = await productService.getPumps();
                return response;
            }
        });
    }

    @action.bound
    onFindSuccess() {
        const parameters = this.queryUtility.plainFilter;
        parameters.inActive = this.queryUtility.filter.inActive ? null : false;
        this.downloadUrl = `${this.service.getDownloadUrl(parameters)}&contentDisposition=attachment&token=${
            this.rootStore.authStore.token.tokenUrl
        }&ttype=${
            this.rootStore.authStore.token.type}`
    }
}

export default InventoryViewStore;
